import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { validateInvite, acceptInvite } from "../services/api";

export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [tokenError, setTokenError] = useState("");

  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError("No invitation token provided.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const data = await validateInvite(token);
        setEmail(data.email || "");
      } catch (err) {
        setTokenError(err?.message || err?.detail || "Invalid or expired invitation.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!fullName.trim()) {
      setError("Full name is required.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      await acceptInvite(token, fullName.trim(), password);
      setDone(true);
    } catch (err) {
      setError(err?.message || err?.detail || "Failed to create account.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <p style={{ textAlign: "center", color: "#6b7280" }}>Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>MAD Discovery</h1>
          <div style={styles.errorBox}>
            <p style={{ margin: 0, fontWeight: 600 }}>Invalid invitation</p>
            <p style={{ margin: "8px 0 0", fontSize: 14 }}>{tokenError}</p>
          </div>
          <button
            onClick={() => navigate("/login")}
            style={{ ...styles.btn, marginTop: 20, background: "#374151" }}
          >
            Go to login
          </button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>MAD Discovery</h1>
          <div style={styles.successBox}>
            <p style={{ margin: 0, fontWeight: 600 }}>Account created</p>
            <p style={{ margin: "8px 0 0", fontSize: 14, color: "#374151" }}>
              Welcome to MAD Discovery! You can now log in.
            </p>
          </div>
          <button
            onClick={() => navigate("/login")}
            style={{ ...styles.btn, marginTop: 20 }}
          >
            Go to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>MAD Discovery</h1>
        <h2 style={styles.subtitle}>Create your account</h2>

        <div style={styles.emailBadge}>
          Invitation for: <strong>{email}</strong>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div style={styles.error}>{error}</div>}

          <label style={styles.label}>Full name</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Your full name"
            required
            autoFocus
            style={styles.input}
          />

          <label style={styles.label}>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
            required
            style={styles.input}
          />

          <label style={styles.label}>Confirm password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repeat password"
            required
            style={styles.input}
          />

          <button type="submit" disabled={busy} style={styles.btn}>
            {busy ? "Creating account..." : "Create account"}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f4f5f7",
    padding: 20,
  },
  card: {
    background: "#fff",
    borderRadius: 16,
    padding: "40px 36px",
    width: "100%",
    maxWidth: 420,
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "#1a1f36",
  },
  subtitle: {
    margin: "8px 0 24px",
    fontSize: 16,
    fontWeight: 500,
    color: "#6b7280",
  },
  emailBadge: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 14,
    color: "#1e40af",
    marginBottom: 20,
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    fontSize: 15,
    border: "1px solid #d1d5db",
    borderRadius: 8,
    marginBottom: 20,
    boxSizing: "border-box",
    outline: "none",
  },
  btn: {
    width: "100%",
    padding: "12px 0",
    fontSize: 15,
    fontWeight: 600,
    color: "#fff",
    background: "#2563eb",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
  },
  error: {
    background: "#fef2f2",
    color: "#991b1b",
    padding: "10px 14px",
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 16,
  },
  errorBox: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 10,
    padding: "20px",
    color: "#991b1b",
  },
  successBox: {
    background: "#ecfdf5",
    border: "1px solid #a7f3d0",
    borderRadius: 10,
    padding: "20px",
    textAlign: "center",
  },
};
